// What an inspector asks about a square: how far the vein under it runs, and
// where the lane through it goes. Read-only and no state of its own — nothing
// in the reducer calls either, so perft and the hash cannot see this file.

import { KING_DIRS } from "../board";
import { pieceAt, type Position } from "../position";
import { LANE_CHASE } from "./belts";
import {
  beltDirAt,
  beltOnAt,
  beltOwnerAt,
  beltTarget,
  depositAt,
  depositYieldAt,
  hasStructure,
  type IndustryState,
  NO_BELT
} from "./state";

/**
 * Every tile of the vein `sq` belongs to, in square order; empty off a field.
 *
 * **Eight-way**, because `growPatch` (`ai/deposits.ts`) grows a patch over
 * `KING_DIRS` — a tile joined only at a corner is part of the field it was
 * carved with, and reading it as its own vein would misreport what is in the
 * ground. The yield word has to match too, so two fields that happen to touch
 * stay two veins.
 */
export function veinTiles(industry: IndustryState, sq: number): number[] {
  if (depositAt(industry, sq) === 0) return [];
  const unit = depositYieldAt(industry, sq);
  const seen = new Set([sq]);
  const stack = [sq];
  while (stack.length > 0) {
    const at = stack.pop()!;
    for (const step of KING_DIRS) {
      const adj = at + step;
      if (seen.has(adj) || depositAt(industry, adj) === 0) continue;
      if (depositYieldAt(industry, adj) !== unit) continue;
      seen.add(adj);
      stack.push(adj);
    }
  }
  // Sorted, never Set order: the answer is a property of the board, not of
  // which tile was asked (PLAN §5.5).
  return [...seen].sort((a, b) => a - b);
}

/**
 * Where the lane through `sq` leads: the squares ahead of it in travel order,
 * ending on the one the last part feeds — a piece, a building or bare ground.
 * Empty where `sq` has no belt, a blueprint being no belt.
 *
 * **`laneDelivers`' walk (belts.ts) drawn rather than answered**: it stops on
 * the same ends, so the two cannot disagree about where a run finishes, and it
 * stays a separate function because the planner calls that one on a hot path
 * and wants only the boolean.
 */
export function laneAhead(
  position: Position,
  industry: IndustryState,
  sq: number
): number[] {
  if (beltDirAt(industry, sq) === NO_BELT) return [];
  const owner = beltOwnerAt(industry, sq);
  const ahead: number[] = [];
  const seen = new Set([sq]);
  let at = sq;
  for (let step = 0; step < LANE_CHASE; step++) {
    const next = beltTarget(position, industry, at);
    // A ring arrives nowhere, so the route ends where it doubles back —
    // `LANE_CHASE` alone would draw the loop sixty-four times.
    if (next === -1 || seen.has(next)) break;
    ahead.push(next);
    seen.add(next);
    // Onward only into more live track of ours; anything else is the end, and
    // the end is part of the route.
    if (hasStructure(industry, next) || pieceAt(position, next) !== 0) break;
    if (beltDirAt(industry, next) === NO_BELT) break;
    if (beltOwnerAt(industry, next) !== owner) break;
    if (beltOnAt(industry, next) !== 1) break;
    at = next;
  }
  return ahead;
}
